import { creditArmsRaceKill, markArmsRaceHit, type DamageCredit } from '../rules/armsRace';
import type { Entity, World } from 'koota';
import { emit, getContext } from '../context';
import { combatAllowed } from '../rules/rounds';
import { Dead, Health } from '../traits';
import type { HitZone, SimEvent } from '../events';
import { weaponByIndex } from '../weapons/defs';
import { playerGeometry } from './geometry';
import { ignoresAttack, sameTeam } from '../rules/party';

export function hitZoneAt(localY: number, height: number, ducking: boolean): HitZone {
  const fromTop = playerGeometry(height, ducking).top - localY;
  if (fromTop <= 0.45) return 'head';
  if (fromTop <= 0.6) return 'neck';
  return 'body';
}

export function damageMultiplier(zone: HitZone, world: World): number {
  const t = getContext(world).tuning;
  if (zone === 'head') return t.headshotMult;
  if (zone === 'neck') return t.neckMult;
  return 1;
}

export function takeDamage(
  world: World,
  target: Entity,
  amount: number,
  zone: HitZone,
  source: number,
  x: number,
  y: number,
  instant = false,
  credit?: DamageCredit,
  attribution?: Pick<Extract<SimEvent, { type: 'kill' }>, 'weaponId' | 'method'>,
): number {
  if (target.has(Dead) || sameTeam(world, source, target)) return 0;
  if (!instant && ignoresAttack(world, source, target)) return 0;
  // Out-of-bounds (instant) kills always apply; everything else waits for the round to be live.
  if (!instant && !combatAllowed(world)) return 0;
  const health = target.get(Health);
  if (!health || health.hp <= 0) return 0;
  const applied = instant ? health.hp : amount * damageMultiplier(zone, world);
  const next = instant ? 0 : Math.max(0, health.hp - applied);
  if (applied > 0) markArmsRaceHit(world, target, source, credit);
  target.set(Health, { hp: next, maxHp: health.maxHp });
  emit(world, { type: 'hit', source, target, damage: applied, zone, x, y });
  emit(world, { type: 'blood', x, y, amount: applied });
  if (next <= 0) {
    // Use the weapon captured by the projectile, even after a throw, death or ladder promotion.
    emit(world, { type: 'kill', source, target, x, y, zone,
      ...(credit && credit.weaponDef >= 0 ? { weaponId: weaponByIndex(credit.weaponDef).id } : { method: source < 0 ? 'hazard' as const : 'punch' as const }),
      ...attribution,
    });
    creditArmsRaceKill(world, source, target, credit);
  }
  return applied;
}
